import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ALL_THEMES, DEFAULT_THEME } from '../config/themes.config';

export type ThemeMode = 'light' | 'dark' | 'system';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private currentTheme = new BehaviorSubject<ThemeMode>('system');
  private currentThemePackage = new BehaviorSubject<string>('default');
  private themePreference: ThemeMode = 'system';
  private themePackageId: string = 'default';
  private mediaQueryList: MediaQueryList | null = null;

  public currentTheme$ = this.currentTheme.asObservable();
  public currentThemePackage$ = this.currentThemePackage.asObservable();

  constructor() {
    this.setupMediaQuery();
    this.initializeTheme();
  }

  private setupMediaQuery(): void {
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.mediaQueryList = window.matchMedia('(prefers-color-scheme: dark)');
      const handleThemeChange = () => this.applyTheme();

      if (typeof this.mediaQueryList.addEventListener === 'function') {
        this.mediaQueryList.addEventListener('change', handleThemeChange);
      } else if (typeof this.mediaQueryList.addListener === 'function') {
        this.mediaQueryList.addListener(handleThemeChange);
      }
    }
  }

  private initializeTheme(): void {
    // Load theme mode preference
    const savedTheme = localStorage.getItem('app-theme') as ThemeMode;
    if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
      this.themePreference = savedTheme;
    } else {
      this.themePreference = 'system';
    }

    // Load theme package preference
    const savedThemePackage = localStorage.getItem('app-theme-package');
    if (savedThemePackage && ALL_THEMES.find(t => t.id === savedThemePackage)) {
      this.themePackageId = savedThemePackage;
    } else {
      this.themePackageId = 'default';
    }

    this.currentTheme.next(this.themePreference);
    this.currentThemePackage.next(this.themePackageId);
    this.applyTheme();
  }

  setTheme(theme: ThemeMode): void {
    this.themePreference = theme;
    localStorage.setItem('app-theme', theme);
    this.currentTheme.next(theme);
    this.applyTheme();
  }

  setThemePackage(packageId: string): void {
    // Validate theme package exists
    if (!ALL_THEMES.find(t => t.id === packageId)) {
      console.warn(`Theme package "${packageId}" not found. Using default instead.`);
      packageId = 'default';
    }

    this.themePackageId = packageId;
    localStorage.setItem('app-theme-package', packageId);
    this.currentThemePackage.next(packageId);
    this.applyTheme();
  }

  private applyTheme(): void {
    const htmlElement = document.documentElement;
    let themeToApply: 'light' | 'dark';

    if (this.themePreference === 'system') {
      // Check system preference, default to 'light' if not available
      themeToApply = this.mediaQueryList?.matches ? 'dark' : 'light';
    } else {
      themeToApply = this.themePreference;
    }

    // Apply Ionic's ion-palette-dark class for dark mode
    if (themeToApply === 'dark') {
      htmlElement.classList.add('ion-palette-dark');
      htmlElement.style.colorScheme = 'dark';
    } else {
      htmlElement.classList.remove('ion-palette-dark');
      htmlElement.style.colorScheme = 'light';
    }

    // Apply theme package via data attribute
    htmlElement.setAttribute('data-theme', this.themePackageId);
  }

  getCurrentTheme(): ThemeMode {
    return this.themePreference;
  }

  getCurrentThemePackage(): string {
    return this.themePackageId;
  }

  getAvailableThemePackages() {
    return ALL_THEMES;
  }

  getEffectiveTheme(): 'light' | 'dark' {
    if (this.themePreference === 'system') {
      return this.mediaQueryList?.matches ? 'dark' : 'light';
    }
    return this.themePreference;
  }

  isDarkMode(): boolean {
    return this.getEffectiveTheme() === 'dark';
  }
}
